import { c as _c } from "react/compiler-runtime";
import { getRequestClient, postOrgOnboardingSession, postOrgOnboardingSessionValidateMutation } from '@onefootprint/fetch-dashboard';
import { bankLinking, onboarding } from '@onefootprint/footprint-js';
import { IcoWarning16 } from '@onefootprint/icons';
import type { ApiError, OrgOnboardingRequest, PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { Button, Divider, InlineAlert, MonacoProvider, Tooltip } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/components/playbook-builder';
import { useNodePanel } from '@/components/playbook-builder/components/node-panel/node-panel-context';
import useHiddenNodeErrors from '@/components/playbook-builder/hooks/use-hidden-node-errors';
import useSidebar from '@/components/playbook-builder/hooks/use-sidebar';
import useOrgSession from '@/hooks/use-org-session';
import useSession from '@/hooks/use-session';
import { isOnboardingEligible } from './components/onboarding/onboarding.utils';
import { OnboardingTestMode } from './components/onboarding/onboarding-test-mode';
import { TestForm, type TestFormValues } from './components/test-form';
import type { OrgOnboardingRequestBody } from './components/test-form/test-form.types';
import { TestModeButton } from './components/test-mode-button';
import { TestPanelHeader } from './components/test-panel-header';
import { ValidatedOnboarding } from './components/validated-user';
import { DEFAULT_BOOTSTRAP_DATA, DEFAULT_OB_DATA, TEST_MODE_NODE_ID } from './test-mode.constants';
type TestModeProps = {
  selectedVersion: PlaybookVersionNoGraph;
  hasPendingChanges: boolean;
  hasErrors: boolean;
  versionId?: string;
};
type TestModeVariantProps = TestModeProps & {
  hasHiddenErrors: boolean;
  onRevealHiddenErrors: () => void;
};
export const TestMode = props => {
  const $ = _c(8);
  const {
    data: t0
  } = useStore();
  const {
    graph
  } = t0;
  const {
    hasHiddenErrors,
    reveal
  } = useHiddenNodeErrors();
  let t1;
  if ($[0] !== hasHiddenErrors || $[1] !== props || $[2] !== reveal) {
    t1 = {
      ...props,
      hasHiddenErrors,
      onRevealHiddenErrors: reveal
    };
    $[0] = hasHiddenErrors;
    $[1] = props;
    $[2] = reveal;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const variantProps = t1;
  if (isOnboardingEligible(props.selectedVersion, graph)) {
    let t2;
    if ($[4] !== variantProps) {
      t2 = <OnboardingTestMode {...variantProps} />;
      $[4] = variantProps;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] !== variantProps) {
    t2 = <LegacyTestMode {...variantProps} />;
    $[6] = variantProps;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const LegacyTestMode = ({
  selectedVersion,
  hasPendingChanges,
  hasErrors,
  hasHiddenErrors,
  onRevealHiddenErrors,
  versionId
}: TestModeVariantProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'testMode'
  });
  const {
    open: openSidebar,
    close: closeSidebar,
    isOpen: isSidebarOpen
  } = useSidebar();
  const {
    setDescriptor,
    bodySlot,
    footerSlot
  } = useNodePanel();
  const isOpen = isSidebarOpen(TEST_MODE_NODE_ID);
  const form = useForm<TestFormValues>({
    defaultValues: {
      bootstrapData: DEFAULT_BOOTSTRAP_DATA,
      onboardingData: DEFAULT_OB_DATA
    }
  });
  const {
    control
  } = form;
  const [lastSubmittedBody, setLastSubmittedBody] = useState<OrgOnboardingRequestBody | null>(null);
  const toggle = () => {
    if (isOpen) closeSidebar();else openSidebar(TEST_MODE_NODE_ID);
  };

  // Transparently add to the payload the playbook version identifier to test whatever graph the user is seeing
  const fixedPayload = hasPendingChanges && versionId ? {
    version_id: versionId
  } : {
    key: `${selectedVersion.key}:v${selectedVersion.version}`
  };
  const {
    authHeaders
  } = useSession();
  const {
    sandbox
  } = useOrgSession();
  const validateMutation = useMutation(postOrgOnboardingSessionValidateMutation());
  const createMutation = useMutation<void, ApiError, OrgOnboardingRequestBody>({
    mutationFn: async (body: OrgOnboardingRequestBody) => {
      validateMutation.reset();

      // Have to specifically omit body case conversion since inputs may have a `.` in them.
      // Maybe would be better to blanket omit case conversion for any key that includes a `.`?
      const client = getRequestClient({
        disableBodySnakeCaseConversion: true
      });
      const {
        data
      } = await postOrgOnboardingSession({
        body: {
          ...body,
          ...fixedPayload
        } as unknown as OrgOnboardingRequest,
        client,
        headers: authHeaders
      });
      const onboardingSessionToken = data.token;
      if (selectedVersion.kind === 'bank_linking') {
        await bankLinking.initialize({
          onboardingSessionToken,
          onComplete: (validationToken: string) => validateMutation.mutate({
            body: {
              validationToken
            }
          }),
          redirectUri: `${window.location.origin}/playbooks/${selectedVersion.playbookId}/bank-linking-callback`
        });
        return;
      }
      await onboarding.initialize({
        onboardingSessionToken,
        onComplete: (validationToken_0: string) => validateMutation.mutate({
          body: {
            validationToken: validationToken_0
          }
        })
      });
    }
  });
  const handleSubmit = async (body_0: OrgOnboardingRequestBody) => {
    try {
      await createMutation.mutateAsync(body_0);
    } finally {
      setLastSubmittedBody(body_0);
    }
  };
  const disabledTooltip = hasErrors && !hasHiddenErrors ? t($ => $.testForm.playbookHasErrors) : undefined;
  const isLoading = createMutation.isPending;
  const fpId = useWatch({
    control,
    name: 'fpId'
  });
  const userExternalId = useWatch({
    control,
    name: 'userExternalId'
  });
  const obExternalId = useWatch({
    control,
    name: 'onboardingExternalId'
  });
  const reusesUser = fpId && lastSubmittedBody?.fp_id === fpId || userExternalId && lastSubmittedBody?.user_external_id === userExternalId;
  const showIdempotencyWarning = reusesUser && (!obExternalId || lastSubmittedBody?.onboarding_external_id === obExternalId);

  // Rides the same inline NodePanel via a synthetic node id, with its own footer (Launch test).
  const body_1 = <>
      <TestPanelHeader title={t($_0 => $_0.testForm.title)} description={t($_1 => $_1.testForm.description)} docHref="https://docs.onefootprint.com/api-reference#post-onboarding-session" />

      {(!sandbox.isSandbox || hasPendingChanges) && <div className="flex flex-col gap-3 px-4 pt-4">
          {!sandbox.isSandbox && <InlineAlert variant="warning">{t($_2 => $_2.testForm.productionAlert)}</InlineAlert>}
          {hasPendingChanges && <InlineAlert variant="neutral">{t($_3 => $_3.pendingChangesAlert)}</InlineAlert>}
        </div>}

      <MonacoProvider languageName="template">
        <FormProvider {...form}>
          <TestForm playbook={selectedVersion} onSubmit={handleSubmit} />
        </FormProvider>
      </MonacoProvider>

      {(!!createMutation.error || validateMutation.data) && <div className="flex flex-col gap-4 pb-4 sticky bottom-0 bg-primary">
          <Divider />
          <div className="flex flex-col px-4 gap-4">
            {!!createMutation.error && <InlineAlert variant="error">
                <div className="flex flex-col gap-1 items-start">
                  <span>{createMutation.error.message}</span>
                  <ul>
                    {createMutation.error.context && Object.entries(createMutation.error.context).map(([key, value]) => <li key={key} className="text-start">
                          <strong>{key}: </strong>
                          <Json data={value} />
                        </li>)}
                  </ul>
                </div>
              </InlineAlert>}
            {validateMutation.data && <ValidatedOnboarding data={validateMutation.data} />}
          </div>
        </div>}
    </>;
  const footer = <div className="flex justify-between w-full">
      <Button variant="secondary" onClick={() => closeSidebar()}>
        {t($_4 => $_4.close)}
      </Button>
      <div className="flex flex-row items-center gap-2">
        {showIdempotencyWarning ? <Tooltip content={t($_5 => $_5.testForm.idempotencyWarning)}>
            <IcoWarning16 color="warning" />
          </Tooltip> : null}
        <Tooltip disabled={!disabledTooltip} content={disabledTooltip}>
          <Button form="test-form" type="submit" loading={isLoading} disabled={isLoading || !!disabledTooltip} onClick={event => {
          if (!hasHiddenErrors) return;
          event.preventDefault();
          onRevealHiddenErrors();
        }}>
            {t($_6 => $_6.testForm.launchTest)}
          </Button>
        </Tooltip>
      </div>
    </div>;
  useEffect(() => {
    if (!isOpen) return;
    setDescriptor({
      nodeId: TEST_MODE_NODE_ID,
      canEdit: true,
      hasCustomFooter: true,
      size: 'lg',
      anchorless: true,
      isDirty: false,
      clearDirty: () => {},
      onClose: closeSidebar
    });
    return () => setDescriptor(current => current?.nodeId === TEST_MODE_NODE_ID ? null : current);
    // Primitives only; body/footer re-render via the portals below.
  }, [isOpen, setDescriptor, closeSidebar]);
  if (selectedVersion.kind === 'bank_linking') {
    return null;
  }
  return <>
      <TestModeButton label={t($_7 => $_7.title)} onClick={toggle} selectedVersion={selectedVersion} />
      {isOpen && bodySlot ? createPortal(body_1, bodySlot) : null}
      {isOpen && footerSlot ? createPortal(footer, footerSlot) : null}
    </>;
};
export default TestMode;
const Json = t0 => {
  const $ = _c(6);
  const {
    data
  } = t0;
  if (typeof data === "string") {
    let t1;
    if ($[0] !== data) {
      t1 = <span>{data}</span>;
      $[0] = data;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== data) {
    t1 = JSON.stringify(data, null, 2);
    $[2] = data;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <code className="text-caption-1">{t1}</code>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};